// components/ui/ReportContentDialog.tsx
// -----------------------------------------------------------------------------
// Report content dialog - allows users to report posts, comments, clubs, or users
// with a reason. Matches mobile's report-content.tsx behavior exactly.
// -----------------------------------------------------------------------------

import { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./Dialog";
import { Button } from "./Button";
import { supabase } from "@/lib/supabase";
import { useToast } from "./Toast";

type ItemType = "post" | "comment" | "club" | "user";

interface ReportContentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: string;
  itemType: ItemType;
}

const itemTypeLabels: Record<ItemType, string> = {
  post: "Post",
  comment: "Comment",
  club: "Club",
  user: "User",
};

export function ReportContentDialog({
  open,
  onOpenChange,
  itemId,
  itemType,
}: ReportContentDialogProps) {
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  const label = itemTypeLabels[itemType];

  const handleSubmit = useCallback(async () => {
    if (isSubmitting || !reason.trim()) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("report_content", {
        body: {
          itemId,
          itemType,
          reason: reason.trim(),
        },
      });

      if (error) {
        console.error("Error reporting content:", error);
        showToast("Error reporting content. Please try again.", "error");
      } else {
        showToast(
          `${label} reported successfully. Thank you for helping keep our community safe.`,
          "success"
        );
        setReason("");
        onOpenChange(false);
      }
    } catch (error) {
      console.error("Error reporting content:", error);
      showToast("Error reporting content. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, reason, itemId, itemType, label, onOpenChange, showToast]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setReason("");
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Why are you reporting this {itemType}?</DialogTitle>
          <DialogDescription>
            A quick note helps us review it for possible guideline violations
            (e.g., harassment, spam, safety issues).
          </DialogDescription>
        </DialogHeader>

        <div className="pt-2">
          <textarea
            className="w-full min-h-[120px] rounded-xl border border-border-tertiary bg-surface-1 px-4 py-3 text-body-1 text-text-primary placeholder:text-text-tertiary resize-none focus:outline-none focus:ring-1 focus:ring-border-secondary"
            placeholder="Describe the issue..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={400}
            disabled={isSubmitting}
          />
          <p className="text-body-3 text-text-tertiary text-right mt-1">
            {reason.length}/400
          </p>
        </div>

        <DialogFooter className="flex-col gap-3 pt-2">
          <Button
            variant="outline"
            className="w-full h-auto py-2 rounded-full"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            className="w-full h-auto py-2 rounded-full"
            onClick={handleSubmit}
            disabled={!reason.trim() || isSubmitting}
          >
            {isSubmitting ? "Reporting..." : "Report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
